import React from 'react';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomTypography from 'components/Fileds/CustomTypography';
import * as Yup from 'yup';
import GenericDialog from '../GenericDialog/GenericDialog';
import { useTheme, Grid, Button, Box, Typography } from '@mui/material';

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFutureLocalDateString = (days = 30) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return getLocalDateString(d);
};

const parseLocalDate = (input) => {
  if (!input) return null;
  // If Yup already coerced it to a Date object, extract local parts to avoid UTC offset
  if (input instanceof Date) {
    if (isNaN(input.getTime())) return null;
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }
  // Otherwise parse from YYYY-MM-DD string
  const parts = String(input).slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

const OffersModal = ({ open, onClose, initialData, onSubmit, loading, currency = '₹' }) => {
  const isEdit = Boolean(initialData);
  const theme = useTheme();

  const OfferSchema = React.useMemo(() => {
    return Yup.object().shape({
      title: Yup.string().required('Title is required'),
      description: Yup.string().required('Description is required'),
      promo_code: Yup.string()
        .matches(/^[A-Z0-9_]+$/, 'Promo Code must be uppercase alphanumeric (underscores allowed)')
        .required('Promo Code is required'),
      discount_type: Yup.string().required('Discount Type is required'),
      discount_value: Yup.number()
        .typeError('Discount Value must be a number')
        .required('Discount Value is required')
        .positive('Discount Value must be positive')
        .when('discount_type', {
          is: 'percent',
          then: (schema) => schema.max(100, 'Percentage discount cannot exceed 100%'),
        }),
      min_order_amount: Yup.number()
        .typeError('Min Order must be a number')
        .min(0, 'Min Order cannot be negative')
        .nullable(),
      max_discount_amount: Yup.number()
        .typeError('Max Discount must be a number')
        .min(0, 'Max Discount cannot be negative')
        .nullable(),
      start_date: Yup.date()
        .required('Start Date is required')
        .test('not-past', 'Start Date cannot be in the past', function (value) {
          // value is the Yup-coerced Date — use parseLocalDate to strip UTC offset
          if (!value) return true;
          const inputDate = parseLocalDate(value);
          if (!inputDate) return false;

          // In edit mode: if the user hasn't changed the original start date, allow it even if it's in the past
          if (isEdit && initialData?.start_date) {
            const originalDate = parseLocalDate(initialData.start_date.slice(0, 10));
            if (originalDate && inputDate.getTime() === originalDate.getTime()) {
              return true;
            }
          }

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return inputDate >= today;
        }),
      expiry_date: Yup.date()
        .required('Expiry Date is required')
        .test('not-before-start', 'Expiry Date must be on or after Start Date', function (value) {
          // value is the Yup-coerced Date for expiry_date
          if (!value) return true;
          const expiryDate = parseLocalDate(value);
          // this.parent.start_date may be a coerced Date or string — parseLocalDate handles both
          const startDate = parseLocalDate(this.parent.start_date);
          if (!startDate || !expiryDate) return true; // don't block if parsing fails
          return expiryDate >= startDate;
        }),
      usage_limit: Yup.number()
        .typeError('Usage Limit must be a number')
        .min(1, 'Usage Limit must be at least 1')
        .nullable(),
      applicable_to: Yup.string().required('Applicable To is required'),
    });
  }, [isEdit, initialData]);

  const discountTypeOptions = [
    { value: 'percent', label: 'Percentage (%)' },
    { value: 'fixed', label: `Flat Amount (${currency})` }
  ];

  const applicableToOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'dine_in', label: 'Dine In' },
    { value: 'booking', label: 'Booking' },
    { value: 'delivery', label: 'Delivery' },
    { value: 'collection_point', label: 'Collection Point' },
    { value: 'pos', label: 'Pickup' }
  ];

  const title = isEdit ? 'Edit Offer' : 'Create New Offer';

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="sm"
      initialValues={{
        title: initialData?.title || '',
        description: initialData?.description || '',
        promo_code: initialData?.promo_code || '',
        discount_type: initialData?.discount_type || 'percent',
        discount_value: initialData?.discount_value || '',
        min_order_amount: initialData?.min_order_amount || '',
        max_discount_amount: initialData?.max_discount_amount || '',
        start_date: initialData?.start_date ? initialData.start_date.slice(0, 10) : getLocalDateString(),
        expiry_date: initialData?.expiry_date ? initialData.expiry_date.slice(0, 10) : getFutureLocalDateString(30),
        usage_limit: initialData?.usage_limit || '',
        applicable_to: initialData?.applicable_to || 'all',
      }}
      validationSchema={OfferSchema}
      onSubmit={(values, formikHelpers) => {
        // Strip out empty optional values or format them for BE
        const formattedPayload = {
          ...values,
          min_order_amount: values.min_order_amount === '' ? 0.00 : parseFloat(values.min_order_amount),
          max_discount_amount: values.max_discount_amount === '' ? null : parseFloat(values.max_discount_amount),
          usage_limit: values.usage_limit === '' ? null : parseInt(values.usage_limit, 10),
          discount_value: parseFloat(values.discount_value),
        };
        onSubmit(formattedPayload, formikHelpers);
      }}
      submitLabel={isEdit ? 'Update Offer' : 'Create Offer'}
      loading={loading}
      requiredFields={['title', 'description', 'promo_code', 'discount_type', 'discount_value', 'start_date', 'expiry_date', 'applicable_to']}
      showCancel={true}
      cancelLabel="Cancel"
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomTypography
              label="Title"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="title"
              value={values.title}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              placeholder="e.g. Weekend Special"
              error={touched.title && Boolean(errors.title)}
              helperText={touched.title && errors.title}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTypography
              label="Description"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              placeholder="Brief description of the offer"
              error={touched.description && Boolean(errors.description)}
              helperText={touched.description && errors.description}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Promo Code"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="promo_code"
              value={values.promo_code}
              onChange={(e) => {
                const upperVal = e.target.value.toUpperCase().replace(/\s/g, '');
                setFieldValue('promo_code', upperVal);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="e.g. SAVE20"
              error={touched.promo_code && Boolean(errors.promo_code)}
              helperText={touched.promo_code && errors.promo_code}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Discount Type"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomSelect
              name="discount_type"
              value={values.discount_type}
              onChange={handleChange}
              onBlur={handleBlur}
              options={discountTypeOptions}
              placeholder="Select Type"
              error={touched.discount_type && Boolean(errors.discount_type)}
              helperText={touched.discount_type && errors.discount_type}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <CustomTypography
              label={values.discount_type === 'percent' ? 'Discount (%)' : `Discount (${currency})`}
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="discount_value"
              inputProps={{ inputMode: 'decimal' }}
              value={values.discount_value}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                setFieldValue('discount_value', val);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="e.g. 10"
              error={touched.discount_value && Boolean(errors.discount_value)}
              helperText={touched.discount_value && errors.discount_value}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <CustomTypography
              label={`Min Order (${currency})`}
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="min_order_amount"
              inputProps={{ inputMode: 'decimal' }}
              value={values.min_order_amount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                setFieldValue('min_order_amount', val);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="e.g. 100"
              error={touched.min_order_amount && Boolean(errors.min_order_amount)}
              helperText={touched.min_order_amount && errors.min_order_amount}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <CustomTypography
              label={`Max Discount (${currency})`}
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="max_discount_amount"
              inputProps={{ inputMode: 'decimal' }}
              value={values.max_discount_amount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                setFieldValue('max_discount_amount', val);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="No limit"
              error={touched.max_discount_amount && Boolean(errors.max_discount_amount)}
              helperText={touched.max_discount_amount && errors.max_discount_amount}
              disabled={values.discount_type === 'fixed'}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Start Date"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="start_date"
              type="date"
              value={values.start_date}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              error={touched.start_date && Boolean(errors.start_date)}
              helperText={touched.start_date && errors.start_date}
              inputProps={{
                min: isEdit && initialData?.start_date
                  ? initialData.start_date.slice(0, 10)
                  : getLocalDateString()
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Expiry Date"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="expiry_date"
              type="date"
              value={values.expiry_date}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              error={touched.expiry_date && Boolean(errors.expiry_date)}
              helperText={touched.expiry_date && errors.expiry_date}
              inputProps={{
                min: values.start_date || getLocalDateString()
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography
              label="Usage Limit"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomTextField
              name="usage_limit"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              value={values.usage_limit}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setFieldValue('usage_limit', val);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="100"
              error={touched.usage_limit && Boolean(errors.usage_limit)}
              helperText={touched.usage_limit && errors.usage_limit}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTypography
              label="Applicable To"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, mt: 1, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
              {[
                { value: 'all', label: 'All Orders' },
                { value: 'dine_in', label: 'Dine In' },
                { value: 'booking', label: 'Booking' },
                { value: 'delivery', label: 'Delivery' },
                { value: 'collection_point', label: 'Collection Point' },
                { value: 'pos', label: 'Pickup' }
              ].map((opt) => {
                const isSelected = opt.value === 'all'
                  ? values.applicable_to === 'all'
                  : values.applicable_to !== 'all' && values.applicable_to.split(',').includes(opt.value);

                return (
                  <Button
                    key={opt.value}
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() => {
                      if (opt.value === 'all') {
                        setFieldValue('applicable_to', 'all');
                      } else {
                        let current = values.applicable_to === 'all' ? [] : values.applicable_to.split(',').filter(Boolean);
                        if (current.includes(opt.value)) {
                          current = current.filter(val => val !== opt.value);
                        } else {
                          current.push(opt.value);
                        }
                        const newValue = current.length === 0 ? 'all' : current.join(',');
                        setFieldValue('applicable_to', newValue);
                      }
                    }}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '20px',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      py: 0.5,
                      minWidth: 'auto',
                      flexShrink: 0,
                      backgroundColor: isSelected ? '#000000' : 'transparent',
                      color: isSelected ? '#ffffff' : '#64748b',
                      borderColor: isSelected ? '#000000' : '#cbd5e1',
                      '&:hover': {
                        backgroundColor: isSelected ? '#222222' : '#f1f5f9',
                        borderColor: isSelected ? '#222222' : '#94a3b8',
                      }
                    }}
                  >
                    {opt.label}
                  </Button>
                );
              })}
            </Box>
            {touched.applicable_to && errors.applicable_to && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.applicable_to}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
    </GenericDialog>
  );
};

export default OffersModal;
